import { useNavigate } from "react-router";
import { Sparkles, LogOut } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { useMemberOnlyDialogStore } from "@/store/memberOnlyDialogStore";
import { useThemeContext } from "./ThemeContext";
import { useAuth } from "@/context/AuthContext";

const DEFAULT_MESSAGE =
  "You need to sign up as a member with a different email to be able to proceed with this service.";

/**
 * Global "Members Only" popup — shown when a logged-in non-member account (venue) hits a
 * member-only API action (favorites, reviews, bookings, etc). Mounted once in RootLayout;
 * triggered from anywhere, including non-component code, via useMemberOnlyDialogStore.
 */
export function MemberOnlyDialog() {
  const { isLight, isGlassy } = useThemeContext();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const open = useMemberOnlyDialogStore((s) => s.open);
  const message = useMemberOnlyDialogStore((s) => s.message);
  const close = useMemberOnlyDialogStore((s) => s.close);

  const handleLogoutAndSignIn = () => {
    close();
    logout();
    navigate("/login", { state: { signIn: true } });
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent
        showCloseButton={false}
        className={`max-w-sm text-center border-0 bg-transparent shadow-none p-0`}
      >
        <div className="relative p-1">
          {/* Glow stays inside this `relative` box (p-1 = the 4px it used to bleed via -inset-1).
              DialogContent is `overflow-x-hidden`, which makes it a vertical scroll container too,
              so a glow drawn outside its box adds scrollable overflow → unwanted scrollbar. */}
          <div
            className={`absolute inset-0 rounded-3xl blur-xl ${
              isGlassy
                ? "bg-gradient-to-r from-[#ff3366]/40 to-pink-500/40"
                : "bg-gradient-to-r from-[#ff3366]/30 to-pink-500/30"
            }`}
          />
          <div
            className={`relative rounded-2xl border p-6 sm:p-7 ${
              isLight ? "bg-white border-gray-200" : "bg-[#0f0f0f] border-[#2a2a2a]"
            }`}
          >
            <DialogHeader className="items-center">
              <div className="relative mb-3">
                <div className="absolute inset-0 rounded-full bg-[#ff3366]/30 blur-md animate-pulse" />
                <div className="relative w-14 h-14 rounded-full bg-gradient-to-br from-[#ff3366] to-pink-500 flex items-center justify-center shadow-lg shadow-pink-500/25">
                  <Sparkles className="text-white" size={24} />
                </div>
              </div>
              <DialogTitle className={isLight ? "text-gray-900" : "text-white"}>Members Only</DialogTitle>
              <DialogDescription asChild>
                <p className={`text-sm leading-relaxed ${isLight ? "text-gray-600" : "text-gray-300"}`}>
                  {message ?? DEFAULT_MESSAGE}
                </p>
              </DialogDescription>
            </DialogHeader>

            <DialogFooter className="flex-col gap-2 sm:flex-col mt-5">
              <button
                type="button"
                onClick={handleLogoutAndSignIn}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg bg-gradient-to-r from-[#ff3366] to-pink-500 text-white text-sm font-semibold hover:opacity-90 transition-opacity"
              >
                <LogOut size={16} />
                Log Out &amp; Sign In as a Member
              </button>
              <button
                type="button"
                onClick={close}
                className={`w-full px-6 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isLight
                    ? "text-gray-500 hover:bg-gray-100"
                    : "text-gray-400 hover:bg-white/[0.06]"
                }`}
              >
                Not now
              </button>
            </DialogFooter>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
